import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Award } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { ASSETS } from "@/lib/api";

const awards = [
  { title: "RE/MAX Platinum Club", note: "Top-tier annual production" },
  { title: "RE/MAX Hall of Fame", note: "Career achievement recognition" },
  { title: "RE/MAX Chairman's Award", note: "Elite performance honour" },
];

const credentials = [
  "Personal Real Estate Corporation",
  "Licensed real estate professional since 2009",
  "Specialist in foreclosure & court-ordered sales",
  "RE/MAX Performance Realty",
  "Serving Surrey, Coquitlam, Burnaby & the Lower Mainland",
];

const highlights = [
  { k: "15+", v: "Years Licensed" },
  { k: "500+", v: "Transactions Closed" },
  { k: "BC", v: "Court-Ordered Focus" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Mike Sidhu, Personal Real Estate Corporation | BC Court-Ordered Sales Authority"
        description="Licensed since 2009 and a RE/MAX Hall of Fame honouree, Mike Sidhu, Personal Real Estate Corporation, is British Columbia's trusted specialist in foreclosure and court-ordered real estate sales."
        path="/about"
      />
      <section className="bg-navy pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="overline">About</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white mt-4 leading-tight max-w-4xl">
              Mike Sidhu
            </h1>
            <p className="font-mono text-sm md:text-base uppercase tracking-[0.22em] text-brass mt-4">Personal Real Estate Corporation</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-white/70 text-lg mt-6 max-w-2xl leading-relaxed">
              British Columbia's trusted authority in court-ordered real estate.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img src={ASSETS.portrait} alt="Mike Sidhu PREC" className="w-full object-cover aspect-[4/5]" />
              <div className="absolute -bottom-5 -right-5 bg-brass text-white px-6 py-4 hidden sm:block">
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em]">Licensed Since</p>
                <p className="font-heading text-3xl leading-none mt-1">2009</p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal><p className="overline">The Story</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-navy mt-3 leading-tight">
                Clarity and confidence when the stakes are highest.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="font-body text-ink-soft text-lg leading-relaxed mt-8 space-y-5">
                <p>
                  Since earning his license in 2009, Mike Sidhu, Personal Real Estate Corporation, has
                  focused his practice where precision matters most: foreclosure and court-ordered
                  sales across the Lower Mainland.
                </p>
                <p>
                  These are not ordinary transactions. They move through the Supreme Court of British
                  Columbia, involve lenders, lawyers and multiple stakeholders, and reward a realtor
                  who understands both the market and the legal framework. Mike has built his reputation
                  as that person — the steady, disclosed, defensible hand developers and lenders return to.
                </p>
                <p>
                  Working under RE/MAX Performance Realty, Mike combines institutional discipline with
                  genuine regional knowledge of Surrey, Coquitlam, Burnaby and the communities in between.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid grid-cols-3 gap-6 mt-12 border-y border-navy/10 py-8">
                {highlights.map((h) => (
                  <div key={h.v}>
                    <p className="font-heading text-3xl md:text-4xl text-brass">{h.k}</p>
                    <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-ink-soft mt-2">{h.v}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10">
                <p className="overline mb-5">Credentials</p>
                <ul className="space-y-3">
                  {credentials.map((c) => (
                    <li key={c} className="flex items-start gap-3 font-body text-navy">
                      <CheckCircle2 size={18} className="text-brass mt-0.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 mt-10 bg-navy text-white px-8 py-4 hover:bg-brass transition-colors duration-300">
                Work With Mike <ArrowRight size={17} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="bg-navy py-20 md:py-28" data-testid="awards-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="overline">Recognition</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mt-3 leading-tight max-w-3xl">
              Honoured among RE/MAX's top performers.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
            {awards.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08} className="bg-navy">
                <div className="p-8 h-full">
                  <Award size={26} className="text-brass" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl text-white mt-5">{a.title}</h3>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-white/50 mt-3">{a.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
